import styled from '@emotion/styled';
import { forwardRef, useRef, useState } from 'react';
import { useButton } from 'react-aria';

import { capabilityUtils } from '../../../lib/capabilities/capabilities';
import { getIsDeviceQuickActionStateOnly } from '../../../lib/deviceQuickAction.ts';
import { mergeRefs } from '../../../lib/mergeRefs';
import { RouteManager } from '../../../RouteManager';
import { ToastManager } from '../../../ToastManager';

import { useI18n } from '../../../hooks/useI18nFormatters.tsx';
import { useQuickAction } from '../../../hooks/useQuickAction.js';
import { useDevice } from '../../../store/devices/useDevices.ts';
import { useApi } from '../../../store/SelectedHomeyStore.hooks.ts';

import { cssUtil } from '../../../theme/cssUtil';
import { glyph } from '../../../theme/glyph';
import { motion } from '../../../theme/motion';
import { theme } from '../../../theme/theme';

import { Battery } from '../../../components/common/Battery';
import { ContextMenu } from '../../../components/common/context-menu/ContextMenu';
import { ContextMenuButton } from '../../../components/common/context-menu/ContextMenuButton';
import { Glyph } from '../../../components/common/Glyph';
import { SwitchButton } from '../../../components/common/SwitchButton';
import { Time } from '../../../components/common/Time';
import { DeviceContextMenuContent } from '../../../components/device/DeviceContextMenuContent';
import { DeviceIcon } from '../../../components/device/DeviceIcon';
import { DeviceNameField } from '../../../components/device/DeviceNameField';
import { PremiumLockedDeviceRow } from '../../../components/premium/PremiumLockedDeviceRow';

import { Table } from './Table';

const contextMenuButtonClass = 'deviceRowContextMenuButton';

const empty = <span className={cssUtil.color.light}>-</span>;

export function DeviceRow(props) {
  const { i18n } = useI18n();
  const { api } = useApi();
  const { device } = useDevice({ deviceId: props.deviceId });

  const deviceName = device?.name.replace(/\s/g, ' ');
  const [name, setName] = useState(deviceName);
  const [isRenaming, setIsRenaming] = useState(false);

  const { value, onQuickAction, quickActionId, quickActionTitle } = useQuickAction({
    device: device,
  });
  const quickActionCapabilityObj =
    quickActionId != null && device?.capabilitiesObj != null
      ? (device.capabilitiesObj[quickActionId] ?? null)
      : null;
  const isQuickActionStateOnly = getIsDeviceQuickActionStateOnly({
    capabilityObj: quickActionCapabilityObj,
    device,
  });
  const actionableQuickActionId = isQuickActionStateOnly === true ? null : quickActionId;

  function renameDevice(event) {
    if (event.currentTarget.value.length === 0) return;

    const prevName = name;
    const nextName = event.currentTarget.value.replace(/\s/g, ' ');

    setName(nextName);
    setIsRenaming(false);

    if (prevName !== nextName) {
      api.devices
        .updateDevice({
          id: device.id,
          device: {
            name: nextName,
          },
        })
        .then(() => {})
        .catch((error) => {
          setName(prevName);
          ToastManager.handleError(error);
        });
    }
  }

  function getRename() {
    return (
      <DeviceNameField
        type="input"
        defaultValue={device.name.replace(/\s/g, ' ')}
        onBlur={(event) => {
          renameDevice(event);
        }}
        onKeyDown={(event) => {
          switch (event.key) {
            case 'Enter':
              event.preventDefault();
              break;
            default:
              break;
          }
        }}
        onKeyUp={(event) => {
          switch (event.key) {
            case 'Enter':
              renameDevice(event);

              break;
            case 'Escape':
              setIsRenaming(false);
              break;
            default:
              break;
          }
        }}
      />
    );
  }

  if (device == null) {
    return null;
  }

  const isLocked = device.requiresPremium && api?.tier !== 'premium';

  function renderTime(date) {
    return i18n.dateFormatter.formatDistanceToNowStrict(date, {
      addSuffix: true,
    });
  }

  return (
    <ContextMenu
      content={
        <DeviceContextMenuContent
          deviceId={props.deviceId}
          context={null}
          quickActionId={actionableQuickActionId}
          quickActionTitle={quickActionTitle}
          isLocked={isLocked}
          onQuickActionRequest={onQuickAction}
          onRenameRequest={() => {
            setTimeout(() => {
              setIsRenaming(true);
            }, 0);
          }}
        />
      }
    >
      {({ isOpen, onContextMenu }) => {
        return (
          <RowButton
            isSelected={props.isSelected}
            data-is-locked={isLocked}
            onPress={(event) => {
              if (isLocked) {
                RouteManager.toPremiumRequiredDialog();
                return;
              }

              if (event.altKey) {
                if (isQuickActionStateOnly === true) {
                  return;
                }

                onQuickAction();
                return;
              }

              props.onControlsClick?.(event, device);
            }}
            onContextMenu={onContextMenu}
          >
            <Table.td style={{ position: 'relative' }}>
              {isLocked && <PremiumLockedDeviceRow zIndex={2} />}

              <DeviceIcon
                iconObj={device.iconObj}
                iconOverride={device.iconOverride}
                size={theme.icon.size_default}
                color={theme.color.mono_700}
              />
            </Table.td>

            <Table.td>{isRenaming ? getRename() : name}</Table.td>

            <Table.td>{props.deviceProps.zoneName ?? empty}</Table.td>

            <Table.td>{props.deviceProps.class}</Table.td>

            <Table.td>{props.deviceProps.app !== null ? props.deviceProps.app : empty}</Table.td>

            <Table.td>
              {props.deviceProps.available === false || props.deviceProps.ready === false ? (
                <Glyph
                  source={glyph.core.exclamationmarkTriangleFill}
                  color={theme.color.error}
                  size={theme.icon.size_default}
                />
              ) : props.deviceProps.status !== null ? (
                isQuickActionStateOnly === true && quickActionCapabilityObj != null ? (
                  capabilityUtils.getFormattedValue({
                    messageFormatter: i18n.messageFormatter,
                    capabilityObj: quickActionCapabilityObj,
                    value: value ?? props.deviceProps.status,
                  })
                ) : (
                  <SwitchButton
                    onPress={() => {
                      onQuickAction();
                    }}
                    isSelected={value ?? props.deviceProps.status}
                  />
                )
              ) : (
                empty
              )}
            </Table.td>

            <Table.td>
              {props.deviceProps.batteryStatus ? (
                <>
                  <Battery percentage={props.deviceProps.batteryStatus.percentage} />
                  {props.deviceProps.batteryStatus.text}
                </>
              ) : (
                empty
              )}
            </Table.td>

            <Table.td>
              {props.deviceProps.batteries !== null ? props.deviceProps.batteries : empty}
            </Table.td>

            <Table.td>
              {props.deviceProps.energy !== null ? `${props.deviceProps.energy} W` : empty}
            </Table.td>

            {props.supportsLastSeenAt && (
              <Table.td>
                {props.deviceProps.lastSeenAt !== null ? (
                  <Time render={renderTime} date={props.deviceProps.lastSeenAt} />
                ) : (
                  empty
                )}
              </Table.td>
            )}

            <Table.td />

            <Table.td>
              <ContextMenuButton
                className={contextMenuButtonClass}
                isOpen={isOpen}
                onPress={onContextMenu}
              />
            </Table.td>
          </RowButton>
        );
      }}
    </ContextMenu>
  );
}

const RowButton = forwardRef(function RowWrapper(props, forwardedRef) {
  const buttonRef = useRef();
  const button = useButton({ ...props, elementType: 'tr' }, buttonRef);

  return (
    <DeviceRow.Root
      {...button.buttonProps}
      ref={mergeRefs([buttonRef, forwardedRef])}
      aria-selected={props.isSelected}
      onContextMenu={props.onContextMenu}
      isLocked={props.isLocked}
    >
      {props.children}
    </DeviceRow.Root>
  );
});

DeviceRow.Root = styled(Table.tr)`
  position: relative;
  outline: none;

  .${contextMenuButtonClass} {
    opacity: 0;
    transition: opacity ${motion.transition.micro_partial};
  }

  &[data-is-locked='true'] {
    ${Table.td}:not(:first-of-type):not(:last-of-type) {
      opacity: 0.3;
      filter: grayscale(1);
    }

    ${Table.td}:first-of-type {
      & :not(:first-of-type) {
        opacity: 0.3;
        filter: grayscale(1);
      }
    }
  }

  &:hover {
    .${contextMenuButtonClass} {
      opacity: 1;
    }

    background-color: ${theme.color.background_hover};
    color: ${theme.color.text};

    td:last-of-type {
      background-color: ${theme.color.background_hover};
    }
  }

  &[aria-selected='true'] {
    background-color: ${theme.color.highlight_background};
    --color: ${theme.color.text};

    td:nth-of-type(2) {
      color: ${theme.color.highlight};
      font-weight: ${theme.fontWeight.medium};
    }
  }
`;
